// core/services/feedbackService.ts — API-backed product feedback board.
//
// Thin fetch wrappers over DRF /api/feedback/. Reads hydrate the feedbackStore;
// mutations patch it. Components must NOT import this directly — use hooks/useFeedback.

import type {
  CreateFeedbackInput,
  FeedbackCategory,
  FeedbackDraftAttachment,
  FeedbackItem,
  FeedbackListItem,
  FeedbackManageInput,
  FeedbackRoadmapInput,
  FeedbackStageInput,
  FeedbackStats,
  FeedbackStatus,
} from '@/core/models/feedback';
import { useFeedbackStore } from '@/core/stores/feedbackStore';
import { api, ApiError } from '@/core/lib/apiClient';

export interface FeedbackQuery {
  category?: FeedbackCategory;
  status?: FeedbackStatus;
  /** 'council' restricts to the moderation-facing categories. */
  scope?: 'council';
  sort?: 'top' | 'new' | 'updated';
  mine?: boolean;
  /** Free-text title/body search — used by the duplicate-search box. */
  q?: string;
  /** Caps the response size — used by the "Trending feedback" rail (?sort=top&limit=3). */
  limit?: number;
}

function toQueryString(q: FeedbackQuery): string {
  const params = new URLSearchParams();
  if (q.category) params.set('category', q.category);
  if (q.status) params.set('status', q.status);
  if (q.scope) params.set('scope', q.scope);
  if (q.sort) params.set('sort', q.sort);
  if (q.mine) params.set('mine', '1');
  if (q.q) params.set('q', q.q);
  if (q.limit) params.set('limit', String(q.limit));
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** Result of a single-item fetch — deliberately NOT a nullable value, so a
 * "does not exist" 404 can never be confused with a transient network/server
 * failure (the repo's 404-vs-other honesty standard). */
export type FeedbackGetResult =
  | { status: 'ok'; item: FeedbackItem }
  | { status: 'not-found' }
  | { status: 'error' };

/** Result of a mutation that refetches the item afterward (delete-stage,
 * remove-relation, remove-attachment): the DELETE itself either succeeded or
 * this never resolves (an exception propagates) — `refreshed: false` means
 * the mutation went through but the store's copy may now be stale. */
export interface RefetchingMutationResult {
  refreshed: boolean;
}

/** Reads the freshest copy of one item straight from the store — used by the
 * optimistic-update helpers below so a rollback patches onto whatever is
 * CURRENT (including any concurrent change that landed in between), never a
 * stale snapshot captured before the request started. */
function currentItem(id: string): FeedbackListItem | undefined {
  return useFeedbackStore.getState().items.find((i) => i.id === id);
}

function currentDetailItem(id: string): FeedbackItem | undefined {
  const item = currentItem(id);
  return item && 'roadmap' in item ? (item as FeedbackItem) : undefined;
}

export const feedbackService = {
  /** Loads the board (public read) and hydrates the store. */
  async list(q: FeedbackQuery = {}): Promise<FeedbackListItem[]> {
    const store = useFeedbackStore.getState();
    store.setLoadState('loading');
    try {
      const items = await api<FeedbackListItem[]>(`/feedback/${toQueryString(q)}`);
      useFeedbackStore.getState().setItems(items);
      useFeedbackStore.getState().setLoadState('ready');
      return items;
    } catch (err) {
      useFeedbackStore.getState().setLoadState('error');
      throw err;
    }
  },

  /** One-off list read that does NOT touch the shared board store — used by
   * the rail panels (trending / roadmap preview) and the duplicate-search box,
   * none of which should stomp the main board's loadState or item list. */
  async peek(q: FeedbackQuery = {}): Promise<FeedbackListItem[]> {
    return api<FeedbackListItem[]>(`/feedback/${toQueryString(q)}`);
  },

  async stats(): Promise<FeedbackStats> {
    return api<FeedbackStats>('/feedback/stats/');
  },

  /** Single item by id (public read). Distinguishes "does not exist" (404)
   * from any other failure — a 500/network error must render an error+retry
   * state, never "not found". */
  async get(id: string): Promise<FeedbackGetResult> {
    try {
      const item = await api<FeedbackItem>(`/feedback/${id}/`);
      useFeedbackStore.getState().upsertItem(item);
      return { status: 'ok', item };
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return { status: 'not-found' };
      return { status: 'error' };
    }
  },

  async create(input: CreateFeedbackInput): Promise<FeedbackItem> {
    const item = await api<FeedbackItem>('/feedback/', { method: 'POST', body: input });
    useFeedbackStore.getState().upsertItem(item);
    return item;
  },

  /** Toggles the viewer's upvote optimistically — patches ONLY
   * {upvoted, upvotes} on the store immediately, and rolls back the SAME two
   * fields (never the whole object) onto whatever is current if the request
   * fails, so a concurrent change (e.g. a comment posted mid-flight) is never
   * clobbered in either direction. */
  async toggleUpvote(id: string): Promise<FeedbackItem> {
    const before = currentItem(id);
    const prevUpvoted = before?.upvoted;
    const prevUpvotes = before?.upvotes;
    if (before) {
      useFeedbackStore.getState().patchItem(id, {
        upvoted: !before.upvoted,
        upvotes: before.upvotes + (before.upvoted ? -1 : 1),
      });
    }
    try {
      const item = await api<FeedbackItem>(`/feedback/${id}/upvote/`, { method: 'POST' });
      useFeedbackStore.getState().upsertItem(item);
      return item;
    } catch (err) {
      if (before) {
        useFeedbackStore.getState().patchItem(id, { upvoted: prevUpvoted!, upvotes: prevUpvotes! });
      }
      throw err;
    }
  },

  /** Toggles the viewer's watch (follow) on the item — field-scoped to
   * `watchers` only. See toggleUpvote for the rollback discipline. */
  async toggleWatch(id: string): Promise<FeedbackItem> {
    const before = currentDetailItem(id);
    const prevWatchers = before?.watchers;
    if (before?.watchers) {
      useFeedbackStore.getState().patchItem(id, {
        watchers: {
          ...before.watchers,
          watching: !before.watchers.watching,
          count: before.watchers.count + (before.watchers.watching ? -1 : 1),
        },
      });
    }
    try {
      const item = await api<FeedbackItem>(`/feedback/${id}/watch/`, { method: 'POST' });
      useFeedbackStore.getState().upsertItem(item);
      return item;
    } catch (err) {
      if (prevWatchers) {
        useFeedbackStore.getState().patchItem(id, { watchers: prevWatchers });
      }
      throw err;
    }
  },

  /** Adds a comment. `internal: true` is staff-only and rejected by the API otherwise. */
  async comment(id: string, body: string, internal = false): Promise<FeedbackItem> {
    const item = await api<FeedbackItem>(`/feedback/${id}/comments/`, {
      method: 'POST',
      body: { body, internal },
    });
    useFeedbackStore.getState().upsertItem(item);
    return item;
  },

  /** Toggles "helpful" on a public comment — field-scoped to the ONE comment's
   * {helpful, helpfulCount} within the `comments` array, computed fresh at
   * both flip and rollback time (see currentItem). `commentId` is the raw
   * integer PK ("42"), not the `fbc-42` display id — the endpoint URL takes
   * the int.
   *
   * Error handling is the caller's job (see FeedbackDetailPage.handleHelpful):
   * the backend returns 404 for BOTH "no such comment" and "internal note,
   * non-staff viewer" (deliberately indistinguishable — see api.py
   * toggle_comment_helpful), so a 404 here is not necessarily a real failure. */
  async toggleCommentHelpful(itemId: string, commentId: string | number): Promise<FeedbackItem> {
    const rawId = String(commentId).replace(/^fbc-/, '');
    const fullCommentId = `fbc-${rawId}`;
    const before = currentDetailItem(itemId)?.comments.find((c) => c.id === fullCommentId);
    if (before) {
      const prevHelpful = before.helpful;
      const prevCount = before.helpfulCount;
      const patchComment = (helpful: boolean, helpfulCount: number) => {
        const item = currentDetailItem(itemId);
        if (!item) return;
        useFeedbackStore.getState().patchItem(itemId, {
          comments: item.comments.map((c) =>
            c.id === fullCommentId ? { ...c, helpful, helpfulCount } : c,
          ),
        });
      };
      patchComment(!prevHelpful, prevCount + (prevHelpful ? -1 : 1));
      try {
        const item = await api<FeedbackItem>(`/feedback/${itemId}/comments/${rawId}/helpful/`, {
          method: 'POST',
        });
        useFeedbackStore.getState().upsertItem(item);
        return item;
      } catch (err) {
        patchComment(prevHelpful, prevCount);
        throw err;
      }
    }
    const item = await api<FeedbackItem>(`/feedback/${itemId}/comments/${rawId}/helpful/`, {
      method: 'POST',
    });
    useFeedbackStore.getState().upsertItem(item);
    return item;
  },

  /** Staff-only status transition, with an optional public status note. */
  async setStatus(id: string, status: FeedbackStatus, statusNote?: string): Promise<FeedbackItem> {
    const item = await api<FeedbackItem>(`/feedback/${id}/status/`, {
      method: 'PATCH',
      body: statusNote === undefined ? { status } : { status, status_note: statusNote },
    });
    useFeedbackStore.getState().upsertItem(item);
    return item;
  },

  /** can_manage-gated edit of tracker fields (priority, product area, platform,
   * found-in-version, status note; pinned/assigned_to are staff-only and 403
   * from the server for a non-staff contributor). */
  async manage(id: string, input: FeedbackManageInput): Promise<FeedbackItem> {
    const item = await api<FeedbackItem>(`/feedback/${id}/`, { method: 'PATCH', body: input });
    useFeedbackStore.getState().upsertItem(item);
    return item;
  },

  /** can_manage-gated roadmap block edit (milestone label/ETA, public update). */
  async setRoadmap(id: string, input: FeedbackRoadmapInput): Promise<FeedbackItem> {
    const item = await api<FeedbackItem>(`/feedback/${id}/roadmap/`, {
      method: 'PATCH',
      body: input,
    });
    useFeedbackStore.getState().upsertItem(item);
    return item;
  },

  async addStage(id: string, title: string): Promise<FeedbackItem> {
    const item = await api<FeedbackItem>(`/feedback/${id}/stages/`, {
      method: 'POST',
      body: { title },
    });
    useFeedbackStore.getState().upsertItem(item);
    return item;
  },

  async updateStage(id: string, stageId: number, input: FeedbackStageInput): Promise<FeedbackItem> {
    const item = await api<FeedbackItem>(`/feedback/${id}/stages/${stageId}/`, {
      method: 'PATCH',
      body: input,
    });
    useFeedbackStore.getState().upsertItem(item);
    return item;
  },

  /** The DELETE itself either succeeds or throws (a genuine mutation
   * failure). Once it succeeds, this refetches the item to pick up the
   * removed stage + recomputed overall progress — a refetch failure here does
   * NOT mean the mutation failed, so it is swallowed and reported back via
   * `refreshed: false` rather than thrown (documented judgment call, item 1
   * of the 2026-08-10 adversarial review: the caller shows "removed — but the
   * page may be out of date, refresh to confirm" rather than a false failure
   * toast). */
  async deleteStage(id: string, stageId: number): Promise<RefetchingMutationResult> {
    await api(`/feedback/${id}/stages/${stageId}/`, { method: 'DELETE' });
    const result = await feedbackService.get(id);
    return { refreshed: result.status === 'ok' };
  },

  /** `order` must be a complete permutation of the item's current stage ids. */
  async reorderStages(id: string, order: number[]): Promise<FeedbackItem> {
    const item = await api<FeedbackItem>(`/feedback/${id}/stages/reorder/`, {
      method: 'POST',
      body: { order },
    });
    useFeedbackStore.getState().upsertItem(item);
    return item;
  },

  async addRelation(id: string, toId: string): Promise<FeedbackItem> {
    const item = await api<FeedbackItem>(`/feedback/${id}/relations/`, {
      method: 'POST',
      body: { to: toId },
    });
    useFeedbackStore.getState().upsertItem(item);
    return item;
  },

  /** See deleteStage's doc comment — same refetch-honesty discipline. */
  async removeRelation(id: string, toId: string): Promise<RefetchingMutationResult> {
    await api(`/feedback/${id}/relations/${toId}/`, { method: 'DELETE' });
    const result = await feedbackService.get(id);
    return { refreshed: result.status === 'ok' };
  },

  async uploadAttachment(id: string, file: File): Promise<FeedbackItem> {
    const form = new FormData();
    form.append('file', file);
    const item = await api<FeedbackItem>(`/feedback/${id}/attachments/`, {
      method: 'POST',
      body: form,
    });
    useFeedbackStore.getState().upsertItem(item);
    return item;
  },

  /** See deleteStage's doc comment — same refetch-honesty discipline. */
  async removeAttachment(id: string, attachmentId: number): Promise<RefetchingMutationResult> {
    await api(`/feedback/${id}/attachments/${attachmentId}/`, { method: 'DELETE' });
    const result = await feedbackService.get(id);
    return { refreshed: result.status === 'ok' };
  },

  /** Staff-only hide/unhide for abuse on this public surface. */
  async toggleRemoved(id: string): Promise<FeedbackItem> {
    const item = await api<FeedbackItem>(`/feedback/${id}/remove/`, { method: 'POST' });
    useFeedbackStore.getState().upsertItem(item);
    return item;
  },

  /** Upload-on-insert for the markdown editor's inline-image button — uploads
   * BEFORE the item exists. Does not touch the store (there is no item yet).
   * The returned id is collected into `draft_attachment_ids` on create() IF
   * the image is still referenced in the body at submit time — see
   * deleteDraftAttachment below for the revoke path when it isn't. */
  async uploadDraftAttachment(file: File): Promise<FeedbackDraftAttachment> {
    const form = new FormData();
    form.append('file', file);
    return api<FeedbackDraftAttachment>('/feedback/attachments/draft/', {
      method: 'POST',
      body: form,
    });
  },

  /** Revokes a draft attachment before it's ever claimed by an item
   * (2026-08-10 second adversarial review, finding B4/W2) — called for any
   * inline image whose markdown was deleted from the body before submit,
   * and for every still-inserted draft when the submit panel is discarded
   * or the reporter navigates to a duplicate instead of posting. Callers
   * treat this as fire-and-forget (best-effort cleanup, mirrors
   * remove_attachment's storage-delete discipline) — a failure here must
   * never block the panel from closing. */
  async deleteDraftAttachment(id: number): Promise<void> {
    await api(`/feedback/attachments/draft/${id}/`, { method: 'DELETE' });
  },

  /** can_manage-gated. Idempotent — adding a label already present is a 200
   * no-op, never an error. */
  async addTag(id: string, label: string): Promise<FeedbackItem> {
    const item = await api<FeedbackItem>(`/feedback/${id}/tags/`, {
      method: 'POST',
      body: { label },
    });
    useFeedbackStore.getState().upsertItem(item);
    return item;
  },

  /** can_manage-gated. The label is sent in the request BODY, not the URL
   * (2026-08-10 second adversarial review, finding B3) — a label containing
   * "/" (e.g. "ui/ux") could be created but never addressed for removal via
   * a path segment. See deleteStage's doc comment for the refetch-honesty
   * discipline (this endpoint returns 204, so the item is refetched here). */
  async removeTag(id: string, label: string): Promise<RefetchingMutationResult> {
    await api(`/feedback/${id}/tags/remove/`, { method: 'POST', body: { label } });
    const result = await feedbackService.get(id);
    return { refreshed: result.status === 'ok' };
  },

  _resetForTest(): void {
    useFeedbackStore.getState().setItems([]);
    useFeedbackStore.getState().setLoadState('idle');
  },
} as const;
